import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ResetPasswordForm from "../components/ResetPasswordForm";
import AuthService from "../services/authService";
import Spinner from "../components/Spinner";
import axios from "axios";

function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [token, setToken] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [resetStatus, setResetStatus] = useState<{ success?: boolean; message?: string }>({});
  const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Validate token on mount with cleanup
  useEffect(() => {
    let isMounted = true;

    const queryParams = new URLSearchParams(location.search);
    const tokenParam = queryParams.get("token");

    if (!tokenParam) {
      if (isMounted) {
        setIsTokenValid(false);
        setResetStatus({
          success: false,
          message: "Invalid or missing reset token. Please request a new password reset.",
        });
      }
      return;
    }

    setToken(tokenParam);
    setIsTokenValid(null);

    const validateToken = async () => {
      try {
        const authService = new AuthService();
        const response = await authService.validateResetToken(tokenParam);

        if (!isMounted) return;

        if (response?.status === 200 && response?.data?.valid) {
          setIsTokenValid(true);
        } else {
          setIsTokenValid(false);
          setResetStatus({
            success: false,
            message:
              response?.data?.message ||
              "The password reset link is invalid or has expired.",
          });
        }
      } catch (error: unknown) {
        console.error("Error validating token:", error);

        if (!isMounted) return;

        let errorMessage = "Unable to verify reset token. Please try again.";

        if (axios.isAxiosError(error)) {
          errorMessage = error.response?.data?.message || errorMessage;
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }

        setIsTokenValid(false);
        setResetStatus({
          success: false,
          message: errorMessage,
        });
      }
    };

    validateToken();

    return () => {
      isMounted = false;
    };
  }, [location]);

  // ✅ Handle password reset
  async function handleResetPassword() {
    if (!token) {
      setResetStatus({
        success: false,
        message: "Invalid or missing reset token. Please request a new password reset.",
      });
      return;
    }

    if (password !== confirmPassword) {
      setResetStatus({
        success: false,
        message: "Passwords do not match.",
      });
      return;
    }

    setIsPending(true);

    try {
      const authService = new AuthService();
      const response = await authService.resetPassword(token, password);

      if (response && response.status === 200) {
        const successMessage =
          response.data?.message || "Password has been reset successfully!";
        setResetStatus({
          success: true,
          message: successMessage,
        });

        setTimeout(() => navigate("/login"), 3000);
      } else {
        const errorCode = response?.data?.code || "UNKNOWN_ERROR";
        const errorMessage =
          response?.data?.message || "Failed to reset password.";

        if (errorCode === "TOKEN_INVALID_OR_EXPIRED") {
          setIsTokenValid(false);
        }

        setResetStatus({
          success: false,
          message: errorMessage,
        });
      }
    } catch (error: unknown) {
      console.error("Error resetting password:", error);

      let errorMessage = "An unexpected error occurred. Please try again.";
      let errorCode = "NETWORK_ERROR";

      if (axios.isAxiosError(error)) {
        const backendError = error.response?.data;
        errorCode = backendError?.code || "NETWORK_ERROR";
        errorMessage = backendError?.message || errorMessage;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      if (errorCode === "TOKEN_INVALID_OR_EXPIRED") {
        setIsTokenValid(false);
      }

      setResetStatus({
        success: false,
        message: errorMessage,
      });
    } finally {
      setIsPending(false);
    }
  }

  // ✅ Handle token regeneration request
  async function handleRegenerateToken() {
    if (!token) {
      setResetStatus({
        success: false,
        message: "No token available to regenerate.",
      });
      return;
    }

    setIsRegenerating(true);
    setResetStatus({});

    try {
      const authService = new AuthService();
      const response = await authService.regenerateResetToken(token);

      if (response && response.status === 200) {
        setResetStatus({
          success: true,
          message: response.data?.message || "Your request has been sent to the admin. Please check your email.",
        });
      } else {
        setResetStatus({
          success: false,
          message: response?.data?.message || "Failed to send regeneration request. Please try again.",
        });
      }
    } catch (error: unknown) {
      console.error("Error regenerating token:", error);

      let errorMessage = "An unexpected error occurred. Please try again.";

      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.message || errorMessage;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setResetStatus({
        success: false,
        message: errorMessage,
      });
    } finally {
      setIsRegenerating(false);
    }
  }

  return (
    <div className="w-full min-h-screen flex flex-col justify-center items-center box-border bg-gradient-to-br from-gray-100 to-cyan-50 py-12 px-4">
      <div className="flex flex-col w-full justify-center items-center">
        {/* ✅ Show spinner while validating token */}
        {isTokenValid === null ? (
          <div className="flex flex-col items-center justify-center text-gray-600">
            <Spinner />
            <p className="mt-4 text-sm">Validating your reset link...</p>
          </div>
        ) : isTokenValid === false ? (
          // ❌ Invalid or expired token
          <div className="bg-white shadow-lg rounded-lg p-8 w-full max-w-md">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Link Expired or Invalid
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                {resetStatus.message ||
                  "The password reset link is invalid or has expired."}
              </p>

              {/* Show regeneration success/error message */}
              {resetStatus.message && resetStatus.success !== undefined && (
                <div
                  className={`mb-4 p-3 rounded-md text-sm ${
                    resetStatus.success
                      ? "bg-green-100 text-green-700 border border-green-200"
                      : "bg-red-100 text-red-700 border border-red-200"
                  }`}
                >
                  {resetStatus.message}
                </div>
              )}

              <div className="space-y-3">
                {/* Request New Link Button */}
                <button
                  onClick={handleRegenerateToken}
                  disabled={isRegenerating}
                  className="w-full inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed"
                >
                  {isRegenerating ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Sending Request...
                    </>
                  ) : (
                    "Request New Link"
                  )}
                </button>

                {/* Go to Login Button */}
                <button
                  onClick={() => navigate("/login")}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Go to Login
                </button>
              </div>
            </div>
          </div>
        ) : (
          // ✅ Valid token → show reset form
          <>
            {resetStatus.message && (
              <div
                className={`mb-6 p-4 rounded-md w-full max-w-md text-center ${
                  resetStatus.success
                    ? "bg-green-100 text-green-700 border border-green-200"
                    : "bg-red-100 text-red-700 border border-red-200"
                }`}
              >
                <p className="font-medium">{resetStatus.message}</p>
                {resetStatus.success && (
                  <p className="text-sm mt-2">Redirecting to login page...</p>
                )}
              </div>
            )}

            <ResetPasswordForm
              handleResetPassword={handleResetPassword}
              password={password}
              setPassword={setPassword}
              confirmPassword={confirmPassword}
              setConfirmPassword={setConfirmPassword}
              isPending={isPending}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default ResetPassword;